import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  confirmOnboardingInstallation,
  onboardingStatus as loadOnboardingStatus,
  team as loadTeam,
  type OnboardingStatus,
  type OnboardingStepId,
  type TeamMember,
} from '../api.ts';
import { InfoTip } from '../components/InfoTip.tsx';
import type { View } from '../components/TopBar.tsx';
import { useAnnounce } from '../hooks/useAnnounce.tsx';
import { errorMessage } from '../messages.ts';

/**
 * L'onboarding guidato in cinque passi (mock 5, `o-step1`…`o-step5`).
 *
 * Le cinque cose che chiede — l'ente, gli orari, il protocollo, i volontari,
 * l'installazione — l'ente può già farle una per una, dalle proprie
 * schermate. Questa pagina non le rifà: mostra **l'ordine** e **quanto manca**,
 * e per i tre passi che hanno già una schermata propria porta lì (mock 5
 * chiede "una sequenza", non "un contenitore", `docs/DECISIONI.md` AN e AZ8).
 *
 * I passi "volontari" e "installazione" non hanno oggi una schermata a sé fra
 * quelle del coordinatore — solo il fornitore può invitare oltre al primo
 * coordinatore, e nessuna pagina mostra lo snippet a chi coordina — quindi
 * restano qui: un elenco di sola lettura della squadra e lo snippet da
 * incollare, non una funzione nuova nascosta dentro l'onboarding.
 *
 * "Salva e riprendi dopo" non ha bisogno di un pulsante: ogni passo scrive
 * nella propria schermata, quindi chiudere questa pagina e riaprirla mostra
 * esattamente il punto in cui si era rimasti.
 */

type Props = {
  headingRef?: React.RefObject<HTMLHeadingElement | null>;
  onNavigate: (view: View) => void;
};

const STEP_TARGET: Partial<Record<OnboardingStepId, View>> = {
  tenant: 'widget',
  hours: 'hours',
  protocol: 'supervision',
};

export function OnboardingPage({ headingRef, onNavigate }: Props) {
  const { t } = useTranslation();
  const { say, announcer } = useAnnounce();
  const [status, setStatus] = useState<OnboardingStatus>();
  const [team, setTeam] = useState<TeamMember[]>();
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [nextStatus, nextTeam] = await Promise.all([loadOnboardingStatus(), loadTeam()]);
      setStatus(nextStatus);
      setTeam(nextTeam.members);
      setFailed(false);
    } catch {
      setFailed(true);
      say(t('onboarding.load_failed'), 'danger');
    }
  }, [say, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleInstallation = async (confirmed: boolean): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      const progress = await confirmOnboardingInstallation(confirmed);
      setStatus((current) =>
        current
          ? {
              ...current,
              steps: progress.steps,
              currentStep: progress.currentStep,
              complete: progress.complete,
            }
          : current,
      );
      say(t(confirmed ? 'onboarding.installation_confirmed' : 'onboarding.installation_reset'));
    } catch (error) {
      say(errorMessage(t, error), 'danger');
    } finally {
      setBusy(false);
    }
  };

  const copySnippet = async (snippet: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(snippet);
      say(t('onboarding.snippet_copied'));
    } catch {
      say(t('onboarding.snippet_copy_failed'), 'danger');
    }
  };

  const doneCount = status ? status.steps.filter((step) => step.done).length : 0;

  return (
    <main className="page" id="main" tabIndex={-1}>
      <h2 ref={headingRef} tabIndex={-1}>
        {t('onboarding.title')}
      </h2>
      <p className="card__sub">{t('onboarding.description')}</p>

      {announcer}
      {failed ? (
        <p>
          <button type="button" className="button" onClick={() => void load()}>
            {t('onboarding.retry')}
          </button>
        </p>
      ) : null}

      <section className="card" aria-labelledby="onboarding-progress-heading">
        <div className="section-head">
          <h3 id="onboarding-progress-heading">{t('onboarding.progress_title')}</h3>
          {status ? (
            <p className="count">
              {t('onboarding.progress_count', { done: doneCount, total: status.steps.length })}
            </p>
          ) : null}
        </div>

        {status === undefined ? (
          <>
            <p className="visually-hidden">{t('onboarding.loading')}</p>
            <span className="skeleton skeleton--wide" aria-hidden="true" />
            <span className="skeleton skeleton--wide" aria-hidden="true" />
          </>
        ) : (
          <>
            {/*
              La barra è decorativa: la frase accanto — "3 di 5completati" —
              porta la stessa informazione a chi non la vede (WCAG 1.1.1), e il
              conteggio nel titolo la ripete come testo, non solo come colore
              (sezione 7bis).
            */}
            <div
              className="onboarding-progress"
              role="img"
              aria-label={t('onboarding.progress_count', {
                done: doneCount,
                total: status.steps.length,
              })}
            >
              {status.steps.map((step) => (
                <span
                  key={step.id}
                  className={
                    step.done
                      ? 'onboarding-progress__segment is-done'
                      : 'onboarding-progress__segment'
                  }
                />
              ))}
            </div>

            {status.complete ? (
              <p className="banner">{t('onboarding.complete')}</p>
            ) : (
              <p className="note">{t('onboarding.continue_hint')}</p>
            )}

            <ol className="plain-list onboarding-steps">
              {status.steps.map((step, index) => {
                const isCurrent = !step.done && status.currentStep === step.id;
                return (
                  <li
                    key={step.id}
                    className={isCurrent ? 'row row--flagged' : 'row'}
                    // Il passo corrente non si affida al solo bordo colorato
                    // (WCAG 1.4.1): lo dice anche a chi usa uno screen
                    // reader, riga per riga, non solo nel paragrafo generico
                    // sopra l'elenco.
                    aria-current={isCurrent ? 'step' : undefined}
                  >
                    <div>
                      <p className="onboarding-steps__label">
                        {t('onboarding.step_number', {
                          number: index + 1,
                          total: status.steps.length,
                        })}
                        {isCurrent ? (
                          <span className="onboarding-steps__current">
                            {' '}
                            · {t('onboarding.step_current')}
                          </span>
                        ) : null}
                      </p>
                      <p className="onboarding-steps__title">
                        {t(`onboarding.step_${step.id}_title`)}
                      </p>
                      <p className="muted">{t(`onboarding.step_${step.id}_hint`)}</p>
                    </div>
                    <div className="onboarding-steps__action">
                      <span className={step.done ? 'badge badge--open' : 'badge badge--closed'}>
                        {t(step.done ? 'onboarding.step_done' : 'onboarding.step_missing')}
                      </span>
                      {STEP_TARGET[step.id] ? (
                        <button
                          type="button"
                          className="button"
                          onClick={() => {
                            const target = STEP_TARGET[step.id];
                            if (target) onNavigate(target);
                          }}
                        >
                          {t(`onboarding.step_${step.id}_go`)}
                        </button>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ol>
          </>
        )}
      </section>

      {/* Passo 4: la squadra, in sola lettura. Nessuna schermata del
          coordinatore invita oggi altri volontari — solo il fornitore lo fa,
          per il primo coordinatore — quindi qui si vede solo chi c'è già. */}
      <section className="card" aria-labelledby="onboarding-team-heading">
        <div className="section-head">
          <div className="section-head__title">
            <h3 id="onboarding-team-heading">{t('onboarding.team_title')}</h3>
            <InfoTip>{t('onboarding.team_hint')}</InfoTip>
          </div>
        </div>
        {team === undefined ? (
          <span className="skeleton skeleton--wide" aria-hidden="true" />
        ) : team.length === 0 ? (
          <p className="empty">{t('onboarding.team_empty')}</p>
        ) : (
          <ul className="plain-list">
            {team.map((member) => (
              <li className="row" key={member.id}>
                <p>{member.displayName}</p>
                <span className={member.isActive ? 'badge badge--open' : 'badge badge--closed'}>
                  {t(`platform.role_${member.role}`, { defaultValue: member.role })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Passo 5: lo snippet vero, e la dichiarazione manuale. Nessuna
          richiesta arriva quando qualcuno lo incolla su un sito esterno:
          resta "fatto" solo se lo dice il coordinatore. */}
      {status ? (
        <section className="card" aria-labelledby="onboarding-install-heading">
          <div className="section-head">
            <div className="section-head__title">
              <h3 id="onboarding-install-heading">{t('onboarding.install_title')}</h3>
              <InfoTip>{t('onboarding.install_hint')}</InfoTip>
            </div>
          </div>
          <fieldset className="snippet-field">
            <legend className="visually-hidden">{t('onboarding.snippet_label')}</legend>
            <pre className="snippet">
              <code>{snippetFor(status.installation)}</code>
            </pre>
          </fieldset>
          <div className="actions">
            <button
              type="button"
              className="button"
              onClick={() => void copySnippet(snippetFor(status.installation))}
            >
              {t('onboarding.snippet_copy')}
            </button>
            <button
              type="button"
              className="button button--primary"
              disabled={busy}
              onClick={() =>
                void toggleInstallation(!status.steps.find((s) => s.id === 'installation')?.done)
              }
            >
              {t(
                status.steps.find((s) => s.id === 'installation')?.done
                  ? 'onboarding.install_undo'
                  : 'onboarding.install_done',
              )}
            </button>
          </div>
        </section>
      ) : null}

      <p className="note">{t('onboarding.resume_hint')}</p>
      <div className="actions">
        <button type="button" className="button" onClick={() => onNavigate('console')}>
          {t('onboarding.close')}
        </button>
      </div>
    </main>
  );
}

function snippetFor(installation: { siteKey: string; scriptSrc: string }): string {
  return `<script src="${installation.scriptSrc}" data-site="${installation.siteKey}" async></script>`;
}
